import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'ducks';
import { isKiosk } from '../utils/platform';
import { Product } from '@inter-ikea-storageone/range';
import { e2e, getQuery } from 'utils/queryUtils';
import { defaultAgeGroup } from '@inter-ikea-storageone/range-smastad';

export type ProductDetailsProps = {
  productId?: string;
  boundingBox?: any;
  cluster: Product[] | undefined;
};

export type InfoMessage = {
  message: string;
  params?: any;
  action?: { text: string; onClick(): void };
};

export type PrintOptionsProps = {
  design: boolean;
  productList: boolean;
  assemblyOverview: boolean;
  showAssemblyOverview: boolean;
};

export type LastSavedConfiguration = {
  id: string;
  ref: string;
  position: { p: { x: number; y: number; z: number }; r: { x: number; y: number; z: number } };
}[];

export interface LayoutState {
  doorsVisible: boolean;
  darkScene: boolean;
  discoMode: boolean;
  showDarkSceneWhenGoingBack: boolean;
  measurementsVisible: boolean;
  showMeasurementsWhenGoingBack: boolean;
  loadingScreenVisible: boolean;
  showScreensaver: boolean;
  selectedProductDetails: ProductDetailsProps | null;
  selectedProductDetailsForVpc: string | null;
  proppingVisible: boolean;
  activeSelectedMenu: string[];
  configurationPossible: boolean;
  roomConfigurationActive: boolean;
  showBannerMessage: boolean;
  infoMessage: InfoMessage | null;
  shownInteriorInfoMessage: boolean;
  showHamburgerMenu: boolean;
  backToGalleryRequest: boolean;
  availabilitySheetVisible: boolean;
  printOptions: PrintOptionsProps;
  invalidDoorPlacement: boolean;
  savePossible: boolean;
  ageGroup: string;
  invalidFramePlacement: boolean;
  lastSavedConfiguration: LastSavedConfiguration;
  bigScreenPlanningEnabled: boolean;
  showProductMenuHeader: boolean;
}

export const initialState: LayoutState = {
  doorsVisible: true,
  darkScene: false,
  discoMode: false,
  showDarkSceneWhenGoingBack: false,
  measurementsVisible: e2e() ? false : true,
  showMeasurementsWhenGoingBack: true,
  loadingScreenVisible: true,
  showScreensaver: false,
  selectedProductDetails: null,
  selectedProductDetailsForVpc: null,
  proppingVisible: true,
  activeSelectedMenu: [],
  configurationPossible: true,
  roomConfigurationActive: false,
  infoMessage: null,
  showBannerMessage: !isKiosk()
    ? !(localStorage.getItem('bannerMessageClosed') === 'true') ||
      getQuery().get('enableBannerMessage') === 'true'
    : false,
  shownInteriorInfoMessage: false,
  showHamburgerMenu: false,
  backToGalleryRequest: false,
  availabilitySheetVisible: false,
  printOptions: {
    design: true,
    productList: true,
    assemblyOverview: false,
    showAssemblyOverview: true
  },
  invalidDoorPlacement: false,
  savePossible: true,
  ageGroup: defaultAgeGroup,
  invalidFramePlacement: false,
  lastSavedConfiguration: [],
  bigScreenPlanningEnabled: false,
  showProductMenuHeader: true
};

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    resetLayout(state) {
      state.doorsVisible = true;
      state.darkScene = false;
      state.measurementsVisible = true;
      state.configurationPossible = true;
      state.roomConfigurationActive = false;
      state.savePossible = true;
    },
    showDoors(state) {
      state.doorsVisible = true;
    },
    hideDoors(state) {
      state.doorsVisible = false;
    },
    darkScene(state) {
      state.darkScene = true;
    },
    lightScene(state) {
      state.darkScene = false;
    },
    showDarkSceneWhenGoingBack(state, action: PayloadAction<boolean>) {
      state.showDarkSceneWhenGoingBack = action.payload;
    },
    showMeasurements(state) {
      state.measurementsVisible = true;
    },
    hideMeasurements(state) {
      state.measurementsVisible = false;
    },
    showMeasurementsWhenGoingBack(state, action: PayloadAction<boolean>) {
      state.showMeasurementsWhenGoingBack = action.payload;
    },
    showLoadingScreen(state) {
      state.loadingScreenVisible = true;
    },
    hideLoadingScreen(state) {
      state.loadingScreenVisible = false;
    },
    showProductDetails(state, action: PayloadAction<ProductDetailsProps>) {
      state.selectedProductDetails = action.payload;
    },
    hideProductDetails(state) {
      state.selectedProductDetails = null;
    },
    showProductDetailsForVpc(state, action: PayloadAction<string>) {
      state.selectedProductDetailsForVpc = action.payload;
    },
    hideProductDetailsForVpc(state) {
      state.selectedProductDetailsForVpc = null;
    },
    showPropping(state) {
      state.proppingVisible = true;
    },
    hidePropping(state) {
      state.proppingVisible = false;
    },
    updateSelectedMenu(state, action: PayloadAction<string>) {
      const menuId = action.payload;
      let newActiveMenu;
      if (menuId === state.activeSelectedMenu[state.activeSelectedMenu.length - 1]) {
        newActiveMenu = state.activeSelectedMenu.filter(f => f !== menuId);
      } else {
        newActiveMenu = [...state.activeSelectedMenu, menuId];
      }
      state.activeSelectedMenu = newActiveMenu;
    },
    setActiveSelectedMenu(state, action: PayloadAction<string[]>) {
      state.activeSelectedMenu = action.payload;
    },
    setConfigurationPossible(state, action: PayloadAction<boolean>) {
      state.configurationPossible = action.payload;
    },
    setRoomConfigurationActive(state, action: PayloadAction<boolean>) {
      state.roomConfigurationActive = action.payload;
    },
    setShowBannerMessage(state, action: PayloadAction<boolean>) {
      state.showBannerMessage = action.payload;
      if (!isKiosk() && !action.payload) {
        localStorage.setItem('bannerMessageClosed', 'true');
      }
    },
    setShowScreensaver(state, action: PayloadAction<boolean>) {
      state.showScreensaver = action.payload;
    },
    showInfoMessage(state, action: PayloadAction<InfoMessage>) {
      state.infoMessage = {
        message: action.payload.message,
        params: action.payload.params,
        action: action.payload.action
      };
    },
    dismissInfoMessage(state) {
      state.infoMessage = null;
    },
    setShownInteriorInfoMessage(state, action: PayloadAction<boolean>) {
      state.shownInteriorInfoMessage = action.payload;
    },
    showHamburgerMenu(state) {
      state.showHamburgerMenu = true;
    },
    hideHamburgerMenu(state) {
      state.showHamburgerMenu = false;
    },
    setPrintOptions(state, action: PayloadAction<PrintOptionsProps>) {
      state.printOptions = {
        design: action.payload.design,
        productList: action.payload.productList,
        assemblyOverview: action.payload.assemblyOverview,
        showAssemblyOverview: action.payload.showAssemblyOverview
      };
    },
    setInvalidSlidingDoorPlacementInfo(state, action: PayloadAction<boolean>) {
      state.invalidDoorPlacement = action.payload;
    },
    setSavePossible(state, action: PayloadAction<boolean>) {
      state.savePossible = action.payload;
    },
    setAgeGroup(state, action: PayloadAction<string>) {
      state.ageGroup = action.payload;
    },
    setDiscoMode(state, action: PayloadAction<boolean>) {
      state.discoMode = action.payload;
    },
    setInvalidFramePlacement(state, action: PayloadAction<boolean>) {
      state.invalidFramePlacement = action.payload;
    },
    setLastSavedConfiguration(state, action: PayloadAction<LastSavedConfiguration>) {
      state.lastSavedConfiguration = action.payload;
    },
    setBigScreenPlanningEnabled(state) {
      state.bigScreenPlanningEnabled = true;
    },
    setBigScreenPlanningDisabled(state) {
      state.bigScreenPlanningEnabled = false;
    },
    showProductMenuHeader(state) {
      state.showProductMenuHeader = true;
    },
    hideProductMenuHeader(state) {
      state.showProductMenuHeader = false;
    }
  }
});

export const isMeasurementsVisible = (state: RootState) => state.layout.measurementsVisible;
export const shouldShowMeasurementsWhenGoingBack = (state: RootState) =>
  state.layout.showMeasurementsWhenGoingBack;
export const isDoorsVisible = (state: RootState) => state.layout.doorsVisible;
export const isSceneDark = (state: RootState) => state.layout.darkScene;
export const shouldShowDarkSceneWhenGoingBack = (state: RootState) =>
  state.layout.showDarkSceneWhenGoingBack;
export const isDiscoModeEnabled = (state: RootState) => state.layout.discoMode;
export const isProppingVisible = (state: RootState) => state.layout.proppingVisible;
export const isConflictMenuOpen = (state: RootState) => state.core.conflicts.length > 0;
export const isLoadingScreenVisible = (state: RootState) => state.layout.loadingScreenVisible;
export const isConfigurationPossible = (state: RootState) => state.layout.configurationPossible;
export const isRoomConfigurationActive = (state: RootState) => state.layout.roomConfigurationActive;
export const isCheckoutAvailable = (state: RootState) =>
  state.layout.savePossible &&
  !state.layout.invalidFramePlacement &&
  state.core.invalidProducts.length === 0;
export const hasShownInteriorInfoMessage = (state: RootState) =>
  state.layout.shownInteriorInfoMessage;
export const isShowGroupNav = (state: RootState) => state.products.selectedGroupId === '';
export const isHamburgerMenuOpen = (state: RootState) => state.layout.showHamburgerMenu;
export const isRoomConfigurationAvailable = (state: RootState) =>
  state.core.conflicts.length === 0 &&
  !state.layout.selectedProductDetails &&
  !state.layout.selectedProductDetailsForVpc &&
  state.core.invalidProducts.length === 0;
export const getScreenshotLayout = createSelector(
  [isRoomConfigurationActive, isDoorsVisible, isProppingVisible, isMeasurementsVisible],
  (roomConfigurationActive, doorsVisible, proppingVisible, measurementsVisible) => {
    return { roomConfigurationActive, doorsVisible, proppingVisible, measurementsVisible };
  }
);
export const getProductDetails = (state: RootState) => state.layout.selectedProductDetails;
export const getProductDetailsForVpc = (state: RootState) =>
  state.layout.selectedProductDetailsForVpc;
export const getActiveSelectedMenu = (state: RootState) => state.layout.activeSelectedMenu;
export const getShowBannerMessage = (state: RootState) => state.layout.showBannerMessage;
export const getInfoMessage = (state: RootState) => state.layout.infoMessage;
export const getPrintOptions = (state: RootState) => state.layout.printOptions;
export const hasInvalidDoorPlacement = (state: RootState) => state.layout.invalidDoorPlacement;
export const getIsSavePossible = (state: RootState) => state.layout.savePossible;
export const getAgeGroup = (state: RootState) => state.layout.ageGroup;
export const hasInvalidFramePlacement = (state: RootState) => state.layout.invalidFramePlacement;
export const lastSavedConfiguration = (state: RootState) => state.layout.lastSavedConfiguration;
export const shouldShowScreensaver = (state: RootState) => state.layout.showScreensaver;
export const isBigScreenPlanningEnabled = (state: RootState) =>
  state.layout.bigScreenPlanningEnabled;
export const shouldShowProductMenuHeader = (state: RootState) => state.layout.showProductMenuHeader;

export const {
  resetLayout,
  showDoors,
  hideDoors,
  darkScene,
  lightScene,
  showDarkSceneWhenGoingBack,
  showMeasurements,
  showMeasurementsWhenGoingBack,
  hideMeasurements,
  showLoadingScreen,
  hideLoadingScreen,
  showProductDetails,
  hideProductDetails,
  showProductDetailsForVpc,
  hideProductDetailsForVpc,
  showPropping,
  hidePropping,
  updateSelectedMenu,
  setActiveSelectedMenu,
  setConfigurationPossible,
  setRoomConfigurationActive,
  setShowBannerMessage,
  showInfoMessage,
  dismissInfoMessage,
  setShownInteriorInfoMessage,
  setShowScreensaver,
  showHamburgerMenu,
  hideHamburgerMenu,
  setPrintOptions,
  setInvalidSlidingDoorPlacementInfo,
  setSavePossible,
  setAgeGroup,
  setDiscoMode,
  setInvalidFramePlacement,
  setLastSavedConfiguration,
  setBigScreenPlanningEnabled,
  setBigScreenPlanningDisabled,
  showProductMenuHeader,
  hideProductMenuHeader
} = layoutSlice.actions;
export default layoutSlice.reducer;
